import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Tag, Plus } from 'lucide-react';
import colors from 'theme/colors';
import promotionsApi from 'api/requests/promotions';
import { useUser } from 'context/user/User';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import PromotionsList from './PromotionsList';
import PromotionSlideOver from './PromotionSlideOver';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function PromotionsPage() {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;
    const { toasts, toast } = useToast();

    const [promotions, setPromotions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [slideOver, setSlideOver] = useState({ open: false, data: null });

    useEffect(() => { if (slug) load(); }, [slug]);

    async function load() {
        setLoading(true);
        setError(null);
        try {
            const res = await promotionsApi.list(slug);
            setPromotions(res.data.data || res.data || []);
        } catch {
            setError('Não foi possível carregar as promoções.');
        } finally {
            setLoading(false);
        }
    }

    function handleSaved(saved) {
        setPromotions(prev => {
            const exists = prev.find(p => p.id === saved.id);
            return exists ? prev.map(p => p.id === saved.id ? saved : p) : [...prev, saved];
        });
        toast?.success(slideOver.data ? 'Promoção atualizada' : 'Promoção criada com sucesso');
        setSlideOver({ open: false, data: null });
    }

    function handleDeleted(id) {
        setPromotions(prev => prev.filter(p => p.id !== id));
    }

    function handleToggleActive(id, newValue) {
        setPromotions(prev => prev.map(p => p.id === id ? { ...p, is_active: newValue } : p));
    }

    if (!slug) {
        return (
            <PageWrapper>
                <NoEstablishmentState>
                    <NoEstablishmentIcon><Tag size={36} color='#999' /></NoEstablishmentIcon>
                    <NoEstablishmentTitle>Nenhum estabelecimento encontrado</NoEstablishmentTitle>
                    <NoEstablishmentHint>Crie um estabelecimento para gerenciar promoções.</NoEstablishmentHint>
                </NoEstablishmentState>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <ToastContainer toasts={toasts} />

            <PageHeader>
                <HeaderLeft>
                    <HeaderIcon><Tag size={20} color={panelAccent(colors.occasionalPurple)} /></HeaderIcon>
                    <div>
                        <PageTitle>Promoções</PageTitle>
                        <PageSubtitle>Gerencie promoções e descontos do seu estabelecimento</PageSubtitle>
                    </div>
                </HeaderLeft>
                <NewBtn onClick={() => setSlideOver({ open: true, data: null })} aria-label='Nova promoção'>
                    <Plus size={20} color='#fff' strokeWidth={2.5} />
                </NewBtn>
            </PageHeader>

            <TableCard>
                {loading ? (
                    <LoadingContainer>
                        <Spinner />
                        <LoadingText>Carregando promoções...</LoadingText>
                    </LoadingContainer>
                ) : error ? (
                    <ErrorState>
                        <ErrorTitle>{error}</ErrorTitle>
                        <RetryBtn onClick={load}>Tentar novamente</RetryBtn>
                    </ErrorState>
                ) : (
                    <PromotionsList
                        promotions={promotions}
                        slug={slug}
                        onEdit={promo => setSlideOver({ open: true, data: promo })}
                        onDeleted={handleDeleted}
                        onToggleActive={handleToggleActive}
                        toast={toast}
                    />
                )}
            </TableCard>

            <PromotionSlideOver
                isOpen={slideOver.open}
                onClose={() => setSlideOver({ open: false, data: null })}
                onSaved={handleSaved}
                slug={slug}
                initialData={slideOver.data}
            />
        </PageWrapper>
    );
}

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${colors.neutralBackground};
`;

const PageHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 16px 14px;
    border-radius: 14px 14px 0 0;
    background: ${colors.neutralLight};
    flex-shrink: 0;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const HeaderIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const PageTitle = styled.h1`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
    line-height: 1.3;
`;

const PageSubtitle = styled.p`
    font-size: 13px;
    color: #666;
    margin: 2px 0 0;
`;

const NewBtn = styled.button`
    width: 44px;
    height: 44px;
    border-radius: 14px;
    background: ${panelAccent(colors.occasionalPurple)};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: transform 0.15s, box-shadow 0.15s;
    &:hover { transform: scale(1.06); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); }
    &:active { transform: scale(0.96); }
    &:focus-visible { outline: 2px solid ${panelAccent(colors.occasionalPurple)}; outline-offset: 3px; }
`;

const TableCard = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 0 0 14px 14px;
    box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.04), 0 2px 12px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    min-height: 0;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    gap: 12px;
`;

const Spinner = styled.div`
    width: 32px; height: 32px;
    border: 3px solid ${colors.neutralDark};
    border-top-color: ${panelAccent(colors.occasionalPurple)};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

const LoadingText = styled.span`font-size: 13px; color: #666;`;

const ErrorState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    gap: 12px;
`;

const ErrorTitle = styled.p`
    font-weight: 600;
    font-size: 15px;
    color: ${colors.nightDark};
    margin: 0;
`;

const RetryBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 8px;
    background: none;
    border: 1.5px solid ${colors.neutralDark};
    color: ${colors.nightDark};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
`;

const NoEstablishmentState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 40px 20px;
    text-align: center;
`;

const NoEstablishmentIcon = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: ${colors.neutralDark};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
`;

const NoEstablishmentTitle = styled.h2`
    font-size: 17px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 6px;
`;

const NoEstablishmentHint = styled.p`
    font-size: 14px;
    color: #666;
    margin: 0;
    max-width: 280px;
`;
